import React, { useState } from "react";
import {
  GATES,
  SEQUENTIAL,
  BLOCKS,
  LABELS,
  defaults,
} from "../engine/registry";
import { defaultGenerator } from "../engine/generator";
import { Field, ConfigFields } from "./Properties";
export default function GeneratorWizard({ settings, onClose, onGenerate }) {
  const [cfg, setCfg] = useState(() =>
      (() => {
        const c = JSON.parse(JSON.stringify(settings || defaultGenerator()));
        delete c.counts.HS; delete c.counts.FS;
        c.clocks = c.clocks.map((k) => ({
          ...k,
          activation: k.activation || "POS_EDGE",
          period: 4, duty: 0.5, phase: 0,
        }));
        return c;
      })(),
    ),
    [step, setStep] = useState(0),
    [error, setError] = useState("");
  const set = (key, value) => setCfg((c) => ({ ...c, [key]: value })),
    count = Object.values(cfg.counts).reduce((a, b) => a + b, 0);
  const generate = () => {
    try {
      onGenerate({...cfg, clocks: cfg.clocks.map(clock => ({...clock, period: 4, duty: 0.5, phase: 0}))});
      setError("");
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className="modal generator-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="generator-heading"
      >
        <header className="modal-header">
          <div>
            <div className="eyebrow">EXERCISE BUILDER</div>
            <h2 id="generator-heading">Generate a circuit</h2>
            <p>
              Choose components and clocks. Terminal names can be edited next.
            </p>
          </div>
          <button
            className="icon-button"
            aria-label="Close generator"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="step-tabs">
          <button
            className={step === 0 ? "active" : ""}
            onClick={() => setStep(0)}
          >
            01 · Components
          </button>
          <button
            className={step === 1 ? "active" : ""}
            onClick={() => setStep(1)}
          >
            02 · Rules & clocks
          </button>
        </div>
        <div className="modal-body">
          {step === 0 ? (
            <>
              <div className="generator-columns">
                {[
                  ["Sequential", SEQUENTIAL],
                  ["Logic gates", GATES],
                  ["Functional blocks", BLOCKS],
                ].map(([label, list]) => (
                  <section key={label}>
                    <h3>{label}</h3>
                    {list.map((type) => (
                      <div className="quantity-row" key={type}>
                        <label htmlFor={`count-${type}`}>{LABELS[type]}</label>
                        <input
                          id={`count-${type}`}
                          type="number"
                          min="0"
                          max="24"
                          value={cfg.counts[type] || 0}
                          onChange={(e) =>
                            set("counts", {
                              ...cfg.counts,
                              [type]: Math.max(
                                0,
                                Math.min(24, +e.target.value),
                              ),
                            })
                          }
                        />
                      </div>
                    ))}
                  </section>
                ))}
              </div>
              <details className="disclosure">
                <summary>Component sizes & individual defaults</summary>
                <div className="config-grid">
                  {Object.entries(cfg.counts)
                    .filter(([, n]) => n > 0)
                    .flatMap(([type, n]) =>
                      Array.from({ length: n }, (_, i) => {
                        const key = `${type}:${i}`;
                        return (
                          <div className="mini-card" key={key}>
                            <h4>
                              {LABELS[type]} {i + 1}
                            </h4>
                            <ConfigFields
                              node={{
                                type,
                                name: type,
                                config: {
                                  ...defaults(type),
                                  initial:
                                    cfg.initial === "random" ? 0 : cfg.initial,
                                  policy: cfg.policy,
                                  trigger: cfg.trigger,
                                  ...cfg.options[type],
                                  ...(cfg.perInstance?.[key] || {}),
                                },
                              }}
                              update={(value) =>
                                set("perInstance", {
                                  ...cfg.perInstance,
                                  [key]: {
                                    ...(cfg.perInstance?.[key] || {}),
                                    ...value,
                                  },
                                })
                              }
                            />
                          </div>
                        );
                      }),
                    )}
                </div>
              </details>
              <p className="helper">
                Every selected component is included. You can change individual
                sizes and settings after generation.
              </p>
            </>
          ) : (
            <div className="generator-settings">
              <section>
                <div className="field-pair">
                  <Field label="Primary input terminals">
                    <input
                      type="number"
                      min="1"
                      max="64"
                      value={cfg.inputCount}
                      onChange={(e) => set("inputCount", +e.target.value)}
                    />
                  </Field>
                  <Field label="Clock sources">
                    <input
                      type="number"
                      min="0"
                      max="8"
                      value={cfg.clockCount}
                      onChange={(e) => set("clockCount", +e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Circuit complexity">
                  <select
                    value={cfg.complexity}
                    onChange={(e) => set("complexity", e.target.value)}
                  >
                    <option value="easy">Easy · shallow logic paths</option>
                    <option value="medium">
                      Medium · shared intermediate logic
                    </option>
                    <option value="hard">Hard · deeper dependencies</option>
                  </select>
                </Field>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={cfg.feedback}
                    onChange={(e) => set("feedback", e.target.checked)}
                  />
                  Allow feedback through flip-flops
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={cfg.validEncoders}
                    onChange={(e) => set("validEncoders", e.target.checked)}
                  />
                  Keep encoder inputs one-hot
                </label>
                <div className="field-pair">
                  <Field label="Default initial Q">
                    <select
                      value={cfg.initial}
                      onChange={(e) =>
                        set(
                          "initial",
                          ["X", "random"].includes(e.target.value)
                            ? e.target.value
                            : +e.target.value,
                        )
                      }
                    >
                      {[0, 1, "X", "random"].map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Default SR forbidden policy">
                  <select
                    value={cfg.policy}
                    onChange={(e) => set("policy", e.target.value)}
                  >
                    {["X", "0", "1", "HOLD"].map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Circuit seed" hint="Use the same seed and component settings to generate the same circuit again. Change it for a different circuit.">
                  <div className="inline-control">
                    <input
                      aria-label="Circuit seed"
                      value={cfg.seed}
                      onChange={(e) => set("seed", e.target.value)}
                    />
                    <button
                      onClick={() =>
                        set("seed", String(Math.floor(Math.random() * 1e9)))
                      }
                    >
                      New seed
                    </button>
                  </div>
                </Field>
              </section>
              <section>
                <h3>Clock configuration</h3>
                {Array.from(
                  { length: Math.max(0, Math.min(8, cfg.clockCount)) },
                  (_, i) => (
                    <div key={i} className="mini-card">
                      <h4>CLK{i + 1}</h4>
                      <ConfigFields
                        fixedClockTiming
                        node={{
                          type: "CLOCK",
                          config: {
                            ...defaults("CLOCK"),
                            ...(cfg.clocks[i] || cfg.clocks[0]),
                          },
                        }}
                        update={(value) => {
                          const clocks = [...cfg.clocks];
                          clocks[i] = {
                            ...defaults("CLOCK"),
                            ...(clocks[i] || clocks[0]),
                            ...value,
                          };
                          set("clocks", clocks);
                        }}
                      />
                    </div>
                  ),
                )}
              </section>
            </div>
          )}
          {error && (
            <div className="error-note" role="alert">
              {error}
            </div>
          )}
        </div>
        <footer className="modal-footer">
          <button
            className="text-button"
            onClick={() => setCfg(defaultGenerator())}
          >
            Reset settings
          </button>
          <span className="helper">
            {count} components + {cfg.clockCount} clocks
          </span>
          {step === 0 ? (
            <button className="primary" onClick={() => setStep(1)}>
              Continue →
            </button>
          ) : (
            <button className="primary" onClick={generate}>
              Generate circuit →
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}
